// @ds/tokens/palette — single source of truth for the new design system.
//
// Editorial palette (Tailwind-derived). Used by:
//   - Every primitive in @ds/primitives, @ds/display, @ds/layout, ...
//   - antdTheme.ts (so AntD widgets globally render with these values)
//   - aliases.ts (the C / F / L short-form catalog API)
//
// Legacy app code in src/components/ and src/pages/ continues to import from
// `./tokens` (the legacy AntD-default palette + editorial mirror). Those 255+
// importers stay untouched until each page migrates to @ds primitives, at
// which point its tokens import switches here too.
//
// Rule: every visual value in @ds comes from this file. No hardcoded hex,
// no inline `padding: "12px 24px"`, no magic numbers like `fontSize: 14`.

// ─── Tones (color ramps for status, pills, banners, side-cards) ──────────
//
// Each tone has 5 shades. The structure is uniform so consumers can write
// `TONE[tone].softBg` and switch tones via a single `tone: ToneName` prop
// without conditional lookups.

export interface ToneRamp {
  /** Saturated brand color — text-on-light, icon, primary fill, dot. */
  base:    string;
  /** Deep variant — text on light backgrounds (better contrast than base). */
  text:    string;
  /** Medium tint — solid badge fill. */
  soft:    string;
  /** Lightest tint — pill / banner backgrounds, hover states. */
  softBg:  string;
  /** Medium-light — pill / card outlines. */
  border:  string;
}

export const TONE = {
  green:  { base: "#16a34a", text: "#166534", soft: "#dcfce7", softBg: "#f0fdf4", border: "#86efac" },
  red:    { base: "#dc2626", text: "#991b1b", soft: "#fee2e2", softBg: "#fef2f2", border: "#fca5a5" },
  blue:   { base: "#2563eb", text: "#1e40af", soft: "#dbeafe", softBg: "#eff6ff", border: "#93c5fd" },
  amber:  { base: "#d97706", text: "#92400e", soft: "#fef3c7", softBg: "#fffbeb", border: "#fcd34d" },
  teal:   { base: "#0d9488", text: "#0f766e", soft: "#ccfbf1", softBg: "#f0fdfa", border: "#5eead4" },
  purple: { base: "#7c3aed", text: "#4c1d95", soft: "#ede9fe", softBg: "#f5f3ff", border: "#c4b5fd" },
  grey:   { base: "#6b7280", text: "#374151", soft: "#f3f4f6", softBg: "#f9fafb", border: "#d1d5db" },

  // Added for surfaces that must colour-code a set larger than the original
  // seven — the companies map legends eight company categories at once, and
  // any two sharing a hue read as one kind. Same ramp construction as above
  // (600 / 800 / 100 / 50 / 300), so they compose with `Box`, `Pill`, `Alert`
  // and `Dot` exactly like the originals.
  //
  // Hues are not free choices: `base` values are picked so that every pair in
  // the twelve is separated by CIEDE2000 ≥ 11.5, which is what
  // `companyMapPoints.test.ts` asserts. An earlier draft used indigo #4f46e5
  // here and it sat 6.5 from `purple` — a shade apart, not a colour apart,
  // and unusable as a distinct marker. `stone` is the replacement: a dark
  // neutral, 15.3 from `grey` and the best-separated candidate under all
  // three dichromacies. Re-tune with care and re-run that test.
  orange: { base: "#ea580c", text: "#9a3412", soft: "#ffedd5", softBg: "#fff7ed", border: "#fdba74" },
  lime:   { base: "#65a30d", text: "#3f6212", soft: "#ecfccb", softBg: "#f7fee7", border: "#bef264" },
  pink:   { base: "#db2777", text: "#9d174d", soft: "#fce7f3", softBg: "#fdf2f8", border: "#f9a8d4" },
  cyan:   { base: "#0891b2", text: "#155e75", soft: "#cffafe", softBg: "#ecfeff", border: "#67e8f9" },
  stone:  { base: "#57534e", text: "#292524", soft: "#f5f5f4", softBg: "#fafaf9", border: "#d6d3d1" },
} as const satisfies Record<string, ToneRamp>;

export type ToneName = keyof typeof TONE;

// ─── Surface (page chrome — backgrounds and borders) ─────────────────────

export const SURFACE = {
  /** Page background — slightly cooler than pure grey. */
  bg:           "#f5f6f7",
  /** Card / modal / popover white. */
  surface:      "#ffffff",
  /** Soft alternate surface — table headers, footer chrome, eyebrow row. */
  surfaceSoft:  "#fafbfc",
  /** Neutral hover (vs blue-tinted). */
  surfaceHover: "#eef0f3",
  /** Primary border — section frames, modal divider, card outline. */
  border:       "#e8eaed",
  /** Subtle hairline — between rows, KVGrid columns. */
  borderSoft:   "#eef0f2",
} as const;

// ─── Text ────────────────────────────────────────────────────────────────

export const TEXT = {
  /** Primary copy, entity names, headings. */
  primary:   "#1f2937",
  /** Descriptions, secondary values. */
  secondary: "#4b5563",
  /** Labels, units, column headers, metadata. */
  muted:     "#6b7280",
  /** Empty-state placeholders, disabled text. */
  faint:     "#9ca3af",
  /** Text on dark backgrounds (sidebar, primary buttons). */
  inverse:   "#ffffff",
} as const;

// ─── Interaction states ───────────────────────────────────────────────────

export const INTERACTION = {
  /** Filled primary-control hover surface. Use when `TONE.blue.base` is the rest state. */
  primaryHover: "#0f6cf5",
  /** Spinner track on saturated primary surfaces. */
  spinnerOnPrimaryTrack: "rgba(255, 255, 255, 0.4)",
} as const;

// ─── Opacity ───────────────────────────────────────────────────────────────

export const OPACITY = {
  /** Disabled control text/chrome when the element still needs to remain visible. */
  disabled: 0.5,
  /** Disabled icon-only controls, where the glyph has less surrounding chrome. */
  disabledIcon: 0.4,
} as const;

// ─── Sidebar (dark navy chrome) ──────────────────────────────────────────

export const SIDEBAR = {
  bg:        "#161c2c",
  surface:   "#1d2438",
  text:      "#cbd5e1",
  textDim:   "#7c8696",
  group:     "#5b6478",
  hover:     "#26304a",
  active:    "#2c3650",
} as const;

// ─── Domain accents (semantic aliases on the tone palette) ───────────────

export const DOMAIN = {
  /** Negative prices, costs, received invoices. Maps to TONE.red. */
  cost:       TONE.red.base,
  /** Positive prices, issued invoices. Maps to TONE.green. */
  revenue:    TONE.green.base,
  /** Correction deltas, changed values. Maps to TONE.amber. */
  correction: TONE.amber.base,
  /** Editorial accent — warm rust (used sparingly: hero highlight, marquee). */
  rust:       "#9A4824",
} as const;

// ─── Charts (@ds/display/LineChart) ──────────────────────────────────────
// Series slots are assigned in FIXED order by series identity (never cycled,
// never re-painted when a series disappears). Order validated 2026-09-15 with
// the dataviz palette validator (light surface, adjacent pairs): all checks
// pass for the 6 slots. A 7th+ series falls back to `overflow`.
export const CHART = {
  series: [TONE.blue.base, TONE.amber.base, TONE.green.base, TONE.purple.base, TONE.orange.base, TONE.teal.base],
  overflow: TONE.grey.base,
  height: { sm: 220, md: 320 },
  lineWidth: 2,
  pointSize: 4,
  /** Max whole-number ticks on an integer y axis (LineChart yInteger). */
  yTicks: 5,
} as const;
export type ChartHeight = keyof typeof CHART.height;

// ─── Typography ──────────────────────────────────────────────────────────

export const FONT_FAMILY = {
  /** Default UI font — Inter webfont with system fallback. */
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  mono: "'SF Mono', Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
  /** Editorial serif — marquee numbers, hero amounts, entity titles. */
  serif: "'Iowan Old Style', 'Palatino', 'Charter', Georgia, serif",
} as const;

export const FONT_SIZE = {
  micro:      10,
  label:      11,
  small:      12,
  /** Editorial half-pixel for sub-lines (AmountHero / Stat sub). */
  caption:    12.5,
  body:       13,
  normal:     14,
  subheading: 16,
  large:      18,
  h2:         22,
  h1:         28,
  /** Editorial display — AmountHero / hero amounts. */
  display:    32,
  /** Page hero title (entity-detail H1). */
  hero:       36,
  /** Marquee numbers. */
  marquee:    48,
} as const;

export const FONT_WEIGHT = {
  regular:  400,
  medium:   500,
  semibold: 600,
  bold:     700,
} as const;

export const LINE_HEIGHT = {
  tight:   1.3,
  normal:  1.5,
  loose:   1.6,
  /** Editorial — hero displays. */
  display: 1.05,
  /** Stat value. */
  snug:    1.15,
  /** Sub-text. */
  relaxed: 1.4,
  /**
   * Collapsed line-height for inline count chips and pill badges where the
   * text baseline must sit at the optical centre of a fixed-height container.
   * Setting line-height to 1 prevents inherited leading from pushing content
   * off-centre inside display:inline-flex wrappers.
   * Consumers: CountChip inside SectionTabs, any future fixed-size badge atom.
   */
  none:    1,
} as const;

export const LETTER_SPACING = {
  /** Uppercase eyebrow labels. */
  eyebrow:      "0.12em",
  tightDisplay: "-0.02em",
  tightStat:    "-0.01em",
  /** Page hero title. */
  tightHero:    "-0.025em",
  /**
   * Inline chip / pill labels.
   * 0.1 px of absolute tracking improves legibility of condensed
   * ALL-CAPS or semibold label text at 11–12 px without the wide gaps
   * of em-based tracking. Applies to Pill, Tag, Badge, and any chip
   * variant that shares the same label rhythm.
   */
  chip:         "0.1px",
} as const;

// ─── Layout (spacing, radius, shadow, motion, controls) ──────────────────

export const SPACE = {
  /** 2 px micro gap — suffix/unit gaps inside chips and display atoms (e.g.
   *  the space between a formatted amount and its currency suffix span). */
  xxs: 2,
  xs:  4,
  sm:  8,
  md:  12,
  lg:  16,
  xl:  24,
  xxl: 32,
} as const;

export const RADIUS = {
  /**
   * Square corners — 0 px.
   * Use for data-tags (Tag, TagOutline) and any element where corner-rounding
   * is contextually wrong (e.g. "data label" vs "status badge").
   * Visually distinct from RADIUS.xs (4 px soft), RADIUS.lg (8 px card),
   * and RADIUS.pill (999 px fully-rounded).
   */
  none: 0,
  xs:   4,
  sm:   6,
  md:   6,
  lg:   8,
  /** Modal / large card. */
  xl:   12,
  /** Pills, fully-rounded chips. */
  pill: 999,
} as const;

export const BORDER_WIDTH = {
  /** Standard DS hairline: frames, dividers, table rules, and default control outlines. */
  hairline: 1,
  /** Spinner ring width for small inline loading affordances. */
  spinner: 1.5,
  /** Heavier rule that closes a column-header row — the line a printed document
   *  draws under its table head. Used by `Table variant="document"`. */
  rule: 1.5,
} as const;

// ─── Chip / pill internal padding ────────────────────────────────────────
//
// Vertical × horizontal padding for inline status chips (Pill, Tag, Badge).
// Named here so every chip primitive uses the same rhythm.
//
// sm: dense row contexts (table cells, condensed lists)
// md: default — section headers, hero bands, standalone labels

export const CHIP_PAD = {
  /** Dense: 2 px vertical, 8 px horizontal. */
  sm: "2px 8px",
  /** Default: 3 px vertical, 10 px horizontal. */
  md: "3px 10px",
  /** Icon–text gap inside a chip. */
  iconGap: 6,
} as const;

// ─── Chip / pill control dimensions ──────────────────────────────────────
//
// Fixed dimensions for count badge chips that appear inside tab labels,
// row badges, and similar inline fixed-size affordances. Distinct from
// CHIP_PAD (which governs variable-width chips) and CONTROL_HEIGHT (which
// governs interactive form controls). Use these when a chip must be a
// specific square/pill minimum dimension regardless of content.

export const CHIP = {
  /**
   * Minimum width AND height for a square count badge chip (px).
   * 16 px (= SPACE.lg) provides enough room for a 2-digit count at
   * FONT_SIZE.label (11 px) without a disproportionately large target.
   * Consumers: CountChip inside SectionTabs, any future inline count badge.
   */
  size: 16,
} as const;

// ─── Activity timeline cadence ────────────────────────────────────────────
//
// Layout values specific to the ActivityLog / CommentThread timeline layout:
// dot+line left column, entry row rhythm, change-table cells.
// Grouped here so ActivityLog and CommentThread share the same spatial rhythm.

export const ACTIVITY = {
  /**
   * Diameter of the kind-dot circle (px).
   * 28 px gives enough room for a 12 px icon with 8 px padding on each side.
   * Used by ActivityLog. Reuse for CommentThread dot variant when added.
   */
  dotSize: 28,
  /**
   * Intentional half-pixel for the activity dot ring; not a shared border-width token.
   * 1.5 px is midway between hairline (1 px) and standard (2 px);
   * thicker than a hairline so the tone color reads on soft backgrounds.
   */
  dotBorderWidth: 1.5,
  /**
   * Gap between the dot column and the entry content (px).
   * 14 px sits between SPACE.md (12) and SPACE.lg (16) and is the
   * empirically ideal distance to align entry text with a 28 px dot.
   * Used by ActivityLog. Reuse for CommentThread dot variant when added.
   */
  entryGap: 14,
  /**
   * Vertical padding inside a change-table row (px).
   * 7 px is midway between SPACE.xs (4) and SPACE.sm (8); it matches the
   * compact rhythm of a diff table without feeling too tight.
   * Also applies to the left (field label) and right (value) cells.
   */
  rowPadY: 7,
  /**
   * Width of the field-label column in the change table (px).
   * Fixed so all field names truncate consistently across entries.
   */
  fieldColWidth: 160,
} as const;

export const SHADOW = {
  xs: "0 1px 2px rgba(0, 0, 0, 0.04)",
  sm: "0 2px 4px rgba(0, 0, 0, 0.06)",
  md: "0 4px 12px rgba(0, 0, 0, 0.08)",
  lg: "0 8px 24px rgba(0, 0, 0, 0.12)",
  /** 3px focus halo around base color. Use with rgba helpers below. */
  focusBlue:  "0 0 0 3px rgba(37, 99, 235, 0.12)",
  focusRed:   "0 0 0 3px rgba(220, 38, 38, 0.12)",
} as const;

export const MOTION = {
  /** AntD ConfigProvider expects strings like "0.12s". */
  fast: "0.12s",
  mid:  "0.2s",
  ease: "cubic-bezier(0.4, 0, 0.2, 1)",
  /** Small inline spinner rotation shorthand. */
  spin: "0.7s linear infinite",
  /**
   * Skeleton pulse animation shorthand — duration + easing + iteration.
   * Applied as the `animation` CSS property on skeleton placeholder blocks.
   * Consumers: AmountHero (value + sub skeletons), future KVGrid / Stat
   * loading states.
   * Use together with the @keyframes injected by the skeleton helper, or
   * against any `@keyframes ds-*-skeleton-pulse` declaration that follows
   * the same 0%/50%/100% opacity pattern.
   */
  skeletonPulse: "1.6s ease-in-out infinite",
} as const;

export const CONTROL_HEIGHT = {
  sm: 24,
  md: 32,
  lg: 40,
} as const;

// ─── Skeleton placeholder dimensions ─────────────────────────────────────
//
// Fixed pixel sizes for skeleton blocks matched to specific typography roles.
// Grouped here so all skeleton-state primitives share the same placeholder
// proportions — avoids per-component "magic number" sizing.
//
// Consumers: AmountHero (heroW/heroH = value block, lineW/lineH = sub-line).
//            StatBand/Stat (statW/statH = value block, statSubW/statSubH = sub-line).

export const SKELETON = {
  /**
   * Width (px) of the hero-amount placeholder block.
   * Matches the visual width of a ~6-digit formatted amount at FONT_SIZE.display.
   * Used by AmountHero value skeleton. Will also apply to any future hero-size
   * monetary readout that enters a loading state.
   */
  heroW: 220,
  /**
   * Height (px) of the hero-amount placeholder block.
   * Matches the cap-height band of FONT_SIZE.display (32 px) with LINE_HEIGHT.display.
   * Used by AmountHero value skeleton.
   */
  heroH: 40,
  /**
   * Width (px) of the sub-line placeholder block.
   * Matches a typical short caption string ("netto, 3 faktury") at FONT_SIZE.caption.
   * Used by AmountHero sub skeleton. Reusable for any caption-size loading line.
   */
  lineW: 120,
  /**
   * Height (px) of the sub-line placeholder block.
   * Matches the cap-height band of FONT_SIZE.caption (12.5 px) with LINE_HEIGHT.relaxed.
   * Used by AmountHero sub skeleton.
   */
  lineH: 11,
  /**
   * Width (px) of a page-header title placeholder.
   * Used by compact/default PageHeader title skeletons.
   */
  pageHeaderTitleW: 200,
  /**
   * Height (px) of a compact PageHeader title placeholder.
   * Matches the compact title cap-height band.
   */
  pageHeaderTitleCompactH: 24,
  /**
   * Height (px) of a default PageHeader title placeholder.
   * Matches the default title cap-height band.
   */
  pageHeaderTitleH: 32,
  /**
   * Width (px) of the editorial PageHeader hero-title placeholder.
   * Wider than amount skeletons because entity names are phrase-like.
   */
  pageHeaderHeroTitleW: 360,
  /**
   * Width (px) of a PageHeader subtitle/meta placeholder.
   */
  pageHeaderSubtitleW: 220,
  /**
   * Width (px) of a Stat cell value placeholder block.
   * Matches the visual width of a typical KPI number (e.g. "1 234 567") at
   * FONT_SIZE.h2 (22 px). Used by StatBand/Stat loading state.
   */
  statW: 92,
  /**
   * Height (px) of a Stat cell value placeholder block.
   * Matches the cap-height band of FONT_SIZE.h2 (22 px) with LINE_HEIGHT.snug.
   * Used by StatBand/Stat loading state.
   */
  statH: 26,
  /**
   * Width (px) of a Stat cell sub-line placeholder block.
   * Matches a typical short sub label (e.g. "+3% vs. LY") at FONT_SIZE.caption.
   * Used by StatBand/Stat sub-line loading state.
   */
  statSubW: 70,
  /**
   * Height (px) of a Stat cell sub-line placeholder block.
   * Matches the cap-height band of FONT_SIZE.caption (12.5 px) with LINE_HEIGHT.relaxed.
   * Equals SKELETON.lineH — the same cap-height band used for AmountHero sub skeleton.
   * Used by StatBand/Stat sub-line loading state.
   */
  statSubH: 11,
  /**
   * Width (px) of a KVGrid row label placeholder block.
   * Matches a typical short eyebrow label (e.g. "Total") at FONT_SIZE.label (11 px)
   * with LETTER_SPACING.eyebrow. Shorter than SKELETON.lineW because labels are
   * uppercase-condensed; 80 px is the empirical fit for ~8-character labels.
   * Used by KVGrid loading state.
   */
  kvLabelW: 80,
  /**
   * Height (px) of a KVGrid row label placeholder block.
   * Matches the cap-height band of FONT_SIZE.label (11 px) with LINE_HEIGHT.tight.
   * Used by KVGrid loading state.
   */
  kvLabelH: 10,
  /**
   * Width (px) of a KVGrid row value placeholder block.
   * Matches a typical short value string (e.g. "1 234,56 zł") at FONT_SIZE.normal (14 px).
   * 120 px provides enough room for most short values without over-sizing the skeleton.
   * Used by KVGrid loading state.
   */
  kvValueW: 120,
  /**
   * Height (px) of a KVGrid row value placeholder block.
   * Matches the cap-height band of FONT_SIZE.normal (14 px) with LINE_HEIGHT.normal.
   * Used by KVGrid loading state.
   */
  kvValueH: 16,
} as const;

// ─── Editorial layout cadence ────────────────────────────────────────────
//
// Page-level rhythm and component-internal spacing for the editorial
// design language (DetailLayout, ListLayout, BlueprintShell, AmountHero,
// Stat, PageHeader). Opt-in per layout, not global.

export const LAYOUT = {
  /** Content max-width — full width on 15" MBP; constrains only on wider. */
  contentMaxWidth: 1440,
  /** Responsive horizontal page gutter. */
  pageGutterX:     "clamp(16px, 3vw, 48px)",

  // Page rhythm
  sectionGap: 56,
  statGap:    40,
  tableRow:   20,

  // Page chrome (DetailLayout / ListLayout)
  pagePadX:        28,
  pagePadY:        20,
  pageBottomPad:   60,
  breadcrumbPadY:  10,
  /** EntityViewHeader strip vertical padding. */
  headerStripPadY: 14,
  /** EntityViewHeader hero body vertical padding. */
  headerHeroPadY:  26,

  // Selects
  /** Minimum EntitySelect trigger width — a select never collapses below this. */
  selectMinWidth: 120,

  // Detail-page responsive stacking
  /** Container width below which DetailLayout body grids stack to one column. */
  detailStackBreakpoint: 860,

  // Peek panel
  peekDefaultWidth: 580,
  peekMinWidth:     420,
  peekMaxWidth:     720,

  // Detail-page sidebar width — used by EntityConfig.view.bodyTemplate when
  // the page is laid out as `${main} ${sidebarWidth}`.
  sidebarWidth: 320,

  // Toolbar
  toolbarSearchWidth: 280,
  /** Gap between icon and label inside Toolbar action buttons. */
  toolbarActionGap: 6,
  /** Vertical padding inside Toolbar action buttons. */
  toolbarPadY: 6,
  /** Horizontal padding for Toolbar link buttons. */
  toolbarLinkPadX: 10,
  /** Horizontal padding for Toolbar primary buttons. */
  toolbarPrimaryPadX: 14,
  /** Height of the vertical divider inside a 32 px Toolbar row. */
  toolbarDividerHeight: 20,

  // PageHeader internal cadence
  /** Gap between PageHeader meta fragments and subtitle fragments. */
  headerMetaGap: 10,
  /** Gap between a PageHeader meta icon and its label. */
  headerMetaIconGap: 6,
  /** Gap between PageHeader chip/pill siblings. */
  headerChipGap: 6,

  // Stat cell internal cadence
  labelGap:     6,
  statLabelGap: 10,
  statValueGap: 12,
  statSubGap:   8,
  statPadY:     22,
  statPadYBot:  24,
  statPadX:     28,
  progressH:    5,
  progressR:    3,
  /**
   * 1 px off-scale value for the gap between the affordance icon (edit/lock)
   * and the label text in `Eyebrow`-style headers. Used by `Field` and `KVGrid`.
   * Empirically tuned — between SPACE.xs (4) and a 6 px gap that would feel
   * too loose for an 11 px label.
   */
  affordanceIconGap: 5,

  // SplitPane divider geometry
  /**
   * Full width (px) of the SplitPane resize divider gutter.
   * 4 px is off-scale between SPACE.xxs (2) and SPACE.xs (4) and matches the
   * standard col-resize handle width across editorial layouts.
   * Consumers: SplitPane divider track.
   */
  dividerWidth:  4,
  /**
   * Width (px) of the visible grip line centred inside the divider gutter.
   * 2 px (= SPACE.xxs) renders a hairline-feeling rod without disappearing
   * on hi-DPI displays.
   * Consumers: SplitPane grip rod inside the divider.
   */
  gripWidth:     2,
  /**
   * Height (px) of the visible grip line centred inside the divider gutter.
   * 24 px is empirically tuned for col-resize UX — long enough to read as a
   * grip affordance, short enough not to dominate the divider visually.
   * Consumers: SplitPane grip rod inside the divider.
   */
  gripHeight:    24,

  // Sticky offsets
  /**
   * Height (px) of the top-level page header (nav bar / app shell chrome).
   * 56 px is the standard height used throughout DetailLayout / ListLayout
   * stickyTop demos and the DetailLayout.test.tsx baseline.
   * Consumers: Sticky primitive (`offset="header"`), DetailLayout, ListLayout.
   */
  headerHeight:     56,
  /**
   * Height (px) of the optional sub-header band that sits directly below the
   * page header (e.g. section-tab strips, secondary toolbars).
   * 40 px matches CONTROL_HEIGHT.lg (40 px) which is the standard AntD Tabs bar.
   * Consumers: Sticky primitive (`offset="subheader"`).
   */
  subheaderHeight:  40,
} as const;

// ─── Z-index layers (closed enum) ────────────────────────────────────────
//
// Canonical stacking contexts for DS primitives. Consumers pick a semantic
// layer name — no raw z-index numbers anywhere outside this map.
//
//   raised       — element sits just above siblings (sticky table header inside
//                  a scroll container; z=1 so it clears normal-flow rows).
//   toolbar      — table toolbar, section header strip; sits above the table
//                  body (z=10).
//   drawerChrome — sticky drawer footer / modal header bar; must clear all
//                  table chrome inside a modal body (z=100).
//   modal        — AntD modal backdrop; must clear all page chrome (z=1000).
//
// These values mirror what AntD uses internally so overlapping surfaces
// render in the correct order without fighting the framework.

export const Z_INDEX = {
  /** Just above normal-flow siblings (e.g. sticky table header row). */
  raised:       1,
  /** Table toolbar / section header strips. */
  toolbar:      10,
  /** Sticky drawer footer or modal inner header bar. */
  drawerChrome: 100,
  /** AntD modal backdrop layer. */
  modal:        1000,
} as const;

export type ZLayer = keyof typeof Z_INDEX;

// ─── Dot sizes ────────────────────────────────────────────────────────────

/**
 * Closed set of dot diameters (px) for `<Dot size>`.
 *
 * | Step | px | Use case |
 * |------|----|----------|
 * | xxs  | 4  | Dense table micro-indicator (e.g. provenance dot) |
 * | xs   | 6  | Compact cell dot (e.g. PriceDot in PipelineColumns) |
 * | sm   | 8  | Default status dot — sidebar, KVGrid, PartyCard rows |
 * | md   | 10 | Larger dot for standalone / hero status indicators |
 *
 * 6 px and 10 px are off-scale from SPACE (which steps 4 → 8 → 12); they are
 * justified because dot diameters are visually-tuned to cap-height at their
 * paired font sizes, not to the spacing rhythm.
 *
 * Consumers: `@ds/display/Dot`.
 */
export const DOT_SIZE = {
  xxs: 4,
  xs:  6,
  sm:  8,
  md:  10,
} as const;

export type DotSize = keyof typeof DOT_SIZE;

// ─── Icon-only button sizes ──────────────────────────────────────────────
//
// Dimensions for square icon-only buttons (no label). Cap-height tuned for
// AntD icons. Independent of tone/color.
//
// Consumers: `@ds/primitives/IconButton`.

export const ICON_BUTTON_SIZE = {
  xxs: 16,
  xs:  18,
  sm:  24,
  md:  32,
} as const;

export const ICON_BUTTON_ICON_SIZE = {
  xxs: 12,
  xs:  14,
  sm:  16,
  md:  18,
} as const;

export type IconButtonSize = keyof typeof ICON_BUTTON_SIZE;

// ─── Map marker sizing ───────────────────────────────────────────────────
//
// Pin diameter, badge diameter, and marker border width for
// `@ds/display/MapMarker`. Independent of DOT_SIZE (which is tuned for
// inline table/sidebar dots, not map pins).
//
// Consumers: `@ds/display/MapMarker`, `@ds/display/Map` (cluster renderer).

export const MARKER = {
  /** Default pin diameter (px). */
  size: 28,
  /** Selected/emphasised pin diameter (px). */
  selectedSize: 34,
  /** Count-badge diameter (px). */
  badgeSize: 16,
  /** Pin border width (px). */
  borderWidth: 2,
  /** Cluster circle diameter (px) — an aggregation of pins, so a step above
   *  `selectedSize` keeps the hierarchy single pin < selected < cluster. */
  clusterSize: 36,
  /** Upward lift (px) for a popover anchored on a marker's coordinate. A pin
   *  stands ~1.2 × its diameter tall above the point it marks (head + the
   *  rotated tip), so a bubble anchored on that same point would cover the
   *  pin; this clears a selected pin with a small gap left over. */
  popoverLift: 48,
} as const;

// ─── Box widths (closed enum) ────────────────────────────────────────────
//
// Canonical fixed-pixel slots for `Box width`. Sparingly used — most
// layouts should be content-sized or use Grid/Inline. Closed enum maps
// to the editorial grid's fixed slots.
//
// Consumers: `@ds/layout/Box`.

export const BOX_WIDTH = {
  auto:    "auto",
  min:     "min-content",
  rail:    "110px",
  label:   "140px",
  /** Anchored bubble (map info window). Wide enough that a category pill —
   *  the widest unbreakable atom a card can contain — fits without forcing a
   *  horizontal scrollbar on the surrounding chrome. */
  popover: "260px",
  sidebar: `${LAYOUT.sidebarWidth}px`,
  full:    "100%",
} as const;
export type BoxWidth = keyof typeof BOX_WIDTH;

/**
 * Scroll boundaries (px) — the vertical partners to `BOX_WIDTH`, for content
 * that must stop growing and start scrolling rather than push its container
 * past the viewport. Paired with `Box`'s `maxHeight` + `scroll="y"`.
 */
export const BOX_MAX_HEIGHT = {
  /** A list inside a narrow rail (a modal's left rail): shows a handful of
   *  lines, scrolls past them rather than owning the whole rail. */
  railList: 160,
  /** An anchored bubble's body (map info window) — the vertical partner to
   *  `BOX_WIDTH.popover`, sized so a card plus a line or two still fits. */
  popover: 320,
} as const;
export type BoxMaxHeight = keyof typeof BOX_MAX_HEIGHT;

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Money-sign color: green for revenue (≥0), red for cost (<0). */
export const amountColor = (v: number | null | undefined): string =>
  (v ?? 0) >= 0 ? DOMAIN.revenue : DOMAIN.cost;

/**
 * Money-sign tone: returns a `ToneName` for callers that need to drive a
 * tone-aware primitive (`<Pill tone>`, `<Stat valueTone>`, etc.) from an
 * amount.
 *
 *   - `null` / `0`  → `"grey"`  (neutral — no signal)
 *   - `> 0`         → `"green"` (revenue)
 *   - `< 0`         → `"red"`   (cost)
 */
export const amountTone = (v: number | null | undefined): ToneName => {
  if (v == null || v === 0) return "grey";
  return v < 0 ? "red" : "green";
};

// ─── Type aliases for convenient prop typing ─────────────────────────────

/** All keys of TONE — typically a discriminator on tone-aware components. */
export type Tone = ToneName;
